/**
 * External Imports
 */

import { connect, useDispatch } from 'react-redux';
import { FC, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAnalyticsApi } from '@shared-web-analytics/react';
import { format } from 'date-fns';
/**
 * Nexus Imports
 */
import { NeuIcon } from '@neutron/react';
/**
 * Internal Imports
 */
import TransitionalBarChart from '../../metricsChart/barChart/TransitionalBarChart';
import { RootState } from '../../../redux/store';
import { setStoplightFilter } from '../../../redux/actions';
import { logReportsClick } from '../../../utils/analyticsHelpers';
/**
 * Global Type Definition Imports
 */
import {
  Filter,
  MetricsData,
  StoplightApiMetrics
} from '../../../config/interfaces';
/**
 * Style Imports
 */
import {
  HeaderContainer,
  TaskMetricContainer,
  TitleContainer
} from '../CustomMetricCharts.styles';

interface StoplightMetricChartProps {
  patientSummaryReportFilter: Filter;
  stoplightMetrics: StoplightApiMetrics;
}

const StoplightMetricChart: FC<StoplightMetricChartProps> = ({
  patientSummaryReportFilter,
  stoplightMetrics
}) => {
  const dispatch = useDispatch();
  const navigateTo = useNavigate();
  const { '*': view } = useParams();
  const { logTrackingEvent } = useAnalyticsApi();

  const metricsData: MetricsData = useMemo(() => {
    return {
      totalRecordsCount: stoplightMetrics.totalRecordsCount,
      metrics: [
        {
          label: 'In Progress',
          value: stoplightMetrics.openedRecordsCount,
          color: '#d7a93d'
        },
        {
          label: 'Completed',
          value: stoplightMetrics.completedRecordsCount,
          color: '#467d20'
        },
        {
          label: 'Deferred',
          value: stoplightMetrics.deferredRecordsCount,
          color: '#cc3435'
        }
      ]
    };
  }, [stoplightMetrics]);

  const openStoplightTable = () => {
    dispatch(
      setStoplightFilter({
        ...patientSummaryReportFilter,
        startDate:
          patientSummaryReportFilter.startDate ??
          format(new Date(), 'yyyy-MM-dd'),
        category: '',
        subCategory: '',
        createdBy: '',
        hasAttachedIssues: false,
        hasUpdate: false
      })
    );
    if (view)
      logTrackingEvent(logReportsClick(view?.replace('/', '-'), 'stoplight'));
    setTimeout(() => {
      navigateTo('/patients/stoplight', {
        state: { from: view }
      });
    }, 1000);
  };

  return (
    <TaskMetricContainer onClick={openStoplightTable}>
      <HeaderContainer style={{ paddingLeft: '3.5em' }}>
        <NeuIcon size="1x" color="green" className="ms-1">
          traffic
        </NeuIcon>
        <TitleContainer style={{ marginLeft: '0.5rem' }}>
          {stoplightMetrics.totalRecordsCount} Stoplights
        </TitleContainer>
      </HeaderContainer>
      {metricsData.metrics.length > 0 && (
        <TransitionalBarChart metricsData={metricsData} />
      )}
    </TaskMetricContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    patientSummaryReportFilter: state.ReportReducer.patientSummaryReportFilter,
    stoplightMetrics: state.StoplightReducer.stoplightMetrics
  };
};
export default connect(mapReduxStateToProps)(StoplightMetricChart);
